import React from 'react';
import { Helmet } from 'react-helmet-async';
import { helmetJsonLdProp } from 'react-schemaorg';
import { FAQPage as FAQPageSchema } from 'schema-dts';

import { useAppContext } from '../AppContext';
import { FaqSubpageContentType } from '../contentTypes';
import { decodeHtml } from '../util';
import { stripHtml } from './util';

export interface FAQPageProps {
  topics?: FaqSubpageContentType['topics'];
}

interface Faq {
  answer: string;
  question: string;
}

// stripHtml rimuove i tag ma non decodifica le entità HTML: senza il decodeHtml nel JSON-LD
// finirebbero stringhe letterali come "perch&eacute;", che safeJsonLdReplacer ri-escaperebbe
// in "perch&amp;eacute;". La normalizzazione va ripetuta dopo il decode, per collassare gli
// spazi non separabili nati da &nbsp;.
const toPlainText = (html?: string): string | undefined => {
  const text = decodeHtml(stripHtml(html))?.replace(/\s+/g, ' ').trim();

  return text || undefined;
};

export const FAQPage = ({ topics }: FAQPageProps) => {
  const { locale } = useAppContext();

  // mainEntity è una lista piatta, quindi il raggruppamento per topic non è rappresentabile.
  // Le coppie incomplete vengono scartate: una Question senza name o senza acceptedAnswer.text
  // è markup invalido, meglio non emettere nulla.
  // Il memo evita di rifare il lavoro a ogni render: toPlainText istanzia un DOMParser per chiamata.
  const faqs = React.useMemo(
    () =>
      (topics ?? [])
        .flatMap((topic) => topic.questions ?? [])
        .map((item) => ({ answer: toPlainText(item.answer), question: toPlainText(item.question) }))
        .filter((faq): faq is Faq => faq.answer !== undefined && faq.question !== undefined),
    [topics],
  );

  if (!faqs.length) {
    return null;
  }

  return (
    <Helmet
      script={[
        helmetJsonLdProp<FAQPageSchema>({
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          inLanguage: locale?.code,
          mainEntity: faqs.map((faq) => ({
            '@type': 'Question',
            acceptedAnswer: {
              '@type': 'Answer',
              text: faq.answer,
            },
            name: faq.question,
          })),
        }),
      ]}
    />
  );
};
